import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ReactElement } from 'react'

import { Button } from '@/components/ui/button'

export interface PaginacaoProps {
  readonly pagina: number
  readonly tamanho: number
  readonly total: number
  readonly aoMudarDePagina: (pagina: number) => void
  readonly desabilitado?: boolean
}

/**
 * Paginação servidor-side.
 *
 * Mostra "1–20 de 143" em vez de só o número da página: em uma lista de
 * clientes, saber quantos existem no total é a informação que o operador
 * realmente usa.
 */
export function Paginacao({
  pagina,
  tamanho,
  total,
  aoMudarDePagina,
  desabilitado = false,
}: PaginacaoProps): ReactElement | null {
  const ultimaPagina = Math.max(1, Math.ceil(total / tamanho))

  if (total === 0) {
    return null
  }

  const primeiro = (pagina - 1) * tamanho + 1
  const ultimo = Math.min(pagina * tamanho, total)

  return (
    <nav
      aria-label="Paginação"
      className="flex flex-wrap items-center justify-between gap-3 text-sm"
    >
      <p className="text-fg-secondary">
        <span className="tabular-money">
          {primeiro}–{ultimo}
        </span>{' '}
        de <span className="tabular-money">{total}</span>
      </p>

      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={desabilitado || pagina <= 1}
          onClick={() => {
            aoMudarDePagina(pagina - 1)
          }}
        >
          <ChevronLeft aria-hidden="true" />
          Anterior
        </Button>

        <span className="text-xs text-fg-muted">
          Página <span className="tabular-money">{pagina}</span> de{' '}
          <span className="tabular-money">{ultimaPagina}</span>
        </span>

        <Button
          variant="secondary"
          size="sm"
          disabled={desabilitado || pagina >= ultimaPagina}
          onClick={() => {
            aoMudarDePagina(pagina + 1)
          }}
        >
          Próxima
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
    </nav>
  )
}
